/**
 * THE ASSIGNMENT ENGINE, bound to this app.
 *
 * `lib/assignmentEngine.js` is the staged, framework-agnostic scoring logic and
 * `data/rep_proficiency.json` is the routing brain (15 reps, built from 2,830
 * won ClickUp records). `assignmentModel.js` is the pure adapter between them
 * and a normalized championship record. THIS file is the only place the three
 * meet: it loads the map, partially applies the model to it, and adds the one
 * thing that needs the browser — reading the audit trail.
 *
 * The map is read at RUNTIME, so the scheduled state backfill improves every
 * suggestion by re-bundling that JSON alone. No code here changes.
 *
 * The governance rules are stated in `assignmentModel.js`, which enforces them.
 */

import REP_MAP from '../../data/rep_proficiency.json'
import TEAM_ROSTER from '../../data/sr_team_roster.json'
import { proposeForUnassigned } from '../../lib/assignmentEngine.js'
import { readAuditTrail } from '../../lib/audit.js'
import {
  ASSIGN_ENTITY,
  needsAssignment as rawNeedsAssignment,
  needsProposal as rawNeedsProposal,
  proposalFor as rawProposalFor,
  proposalForReassign as rawProposalForReassign,
  repsFrom,
  teamFrom,
  groupedTeamFrom,
} from './assignmentModel.js'

export {
  ASSIGN_FIELD, ASSIGN_ENTITY, ASSIGN_ACTION,
  currentRep, toOpportunity, confidenceMeta, CONFIDENCE_META, assignmentAudit,
} from './assignmentModel.js'

export { REP_MAP, TEAM_ROSTER }

/* ---- the model, bound to the shipped map ----------------------------- */

export const needsAssignment = rawNeedsAssignment
export const needsProposal = rawNeedsProposal

/** The proposal for a record — NULL when it already has an owner. */
export function proposalFor(record, map = REP_MAP) {
  return rawProposalFor(record, map)
}

/** A proposal regardless of ownership, for the reassignment picker only. */
export function proposalForReassign(record, map = REP_MAP) {
  return rawProposalForReassign(record, map)
}

/* ---- the proven reps (the SUGGESTION side) --------------------------- */

export const REPS = repsFrom(REP_MAP)

/** Just the names, for a `<select>`. */
export const REP_NAMES = REPS.map((r) => r.name)

/** How many reps the map actually loaded — surfaced so an empty map is visible. */
export const REP_COUNT = REPS.length

/* ---- the full team (the PICKER side) --------------------------------- */

/**
 * EVERY assignable SR team member, not just the 15 who have won something.
 *
 * A record can belong to anyone doing the work — drop artwork on a designer, a
 * gym partnership on the cheer team, a pursuit on a rep — so the picker offers
 * the whole roster while the engine keeps suggesting only from proven wins.
 * Two lists, on purpose; see `assignmentModel.js`.
 *
 * Read at RUNTIME like the routing map: re-bundling `sr_team_roster.json` when
 * somebody joins or leaves is the entire change.
 */
export const TEAM = teamFrom(TEAM_ROSTER)

/** The roster bucketed for `<optgroup>`, in the roster's own group order. */
export const TEAM_GROUPS = groupedTeamFrom(TEAM_ROSTER)

/** Flat display names — the values a write can produce. */
export const TEAM_NAMES = TEAM.map((m) => m.name)

/** How many people the picker actually offers. Zero would be visible, not silent. */
export const TEAM_COUNT = TEAM.length

/** Roster provenance, stated next to the picker like the map's is. */
export const TEAM_META = {
  generated: TEAM_ROSTER.meta?.generated || '',
  source: TEAM_ROSTER.meta?.source || '',
}

/** Map provenance, shown next to the suggestions so nobody guesses at its age. */
export const MAP_META = {
  generated: REP_MAP.meta?.generated || '',
  source: REP_MAP.meta?.source || '',
  stateCoverage: REP_MAP.meta?.state_coverage_pct ?? null,
  note: REP_MAP.meta?.note || '',
}

/**
 * The single-point dependencies the map flags — cheer sitting 90% on one rep,
 * hockey and class rings on a sole proven owner. Surfaced as FLAGS on the
 * Assignments view, never as blockers: they are the reason to cross-train, not
 * a reason to stop routing.
 */
export const SINGLE_POINTS = REP_MAP.assignment_signals?.single_point_dependencies || []

/* ---- the seeded worklist --------------------------------------------- */

/**
 * THE 59 NO-REP WON ACCOUNTS the map ships with, each with its proposal.
 *
 * These are ClickUp account records, not HubSpot 2-64425303 rows, so they carry
 * a ClickUp `url` and NO id the `champion-update` write could target. The view
 * says so and links out rather than offering a button that would 400.
 */
export function seededWorklist(map = REP_MAP) {
  return proposeForUnassigned(map)
}

export const SEEDED_COUNT =
  REP_MAP.assignment_signals?.unassigned_wins?.count ??
  (REP_MAP.assignment_signals?.unassigned_wins?.accounts || []).length

/* ---- the audit trail ------------------------------------------------- */

/**
 * Every assignment change this session, newest first — the from → to → who →
 * when history the drawer shows on a record.
 *
 * It reads the SHARED audit trail (`lib/audit.js`) rather than keeping a second
 * log of its own: the API layer files one event for every non-GET automatically,
 * so there is exactly one record of what happened and no way for the two to
 * drift. The same honest caveat applies as everywhere else in this platform —
 * a client-held trail is evidence of nothing until sr-portal-backend persists
 * it server-side (see CLAUDE.md).
 */
export function assignmentHistory(recordId = null) {
  const id = recordId == null ? null : String(recordId)
  return readAuditTrail().filter(
    (e) => e.entity === ASSIGN_ENTITY && (id == null || e.entityId === id)
  )
}
